import {
  Directive,
  Input,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { Table } from 'primeng/table';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

interface ScrollPosition {
  scrollTop: number;
  scrollLeft: number;
  rowId?: string;
}

/**
 * Directive to automatically save and restore scroll position for PrimeNG p-table.
 *
 * Usage:
 * <p-table pTableScrollRestore="my-unique-table-id" [pTableScrollData]="myDataArray" ...>
 *
 * Required:
 * - pTableScrollRestore: A unique string ID for this table (used as storage key)
 * - pTableScrollData: The data array bound to the table (needed to find row index for page changes)
 *
 * Optional:
 * - pTableScrollDataKey: The property name to use as row ID (default: 'id')
 *
 * The directive will:
 * - Automatically save scroll position on scroll
 * - Automatically restore scroll position on navigation back (popstate)
 * - Handle pagination automatically
 */
@Directive({
  selector: '[pTableScrollRestore]',
})
export class PTableScrollRestoreDirective implements AfterViewInit, OnDestroy {
  @Input('pTableScrollRestore') tableId!: string;
  @Input() pTableScrollData: any[] = [];
  @Input() pTableScrollDataKey = 'id';

  private destroy$ = new Subject<void>();
  private scrollableBody: HTMLElement | null = null;
  private scrollEventAttached = false;
  private pendingScrollRowId: string | null = null;
  private storageKeyPrefix = 'ptable-scroll-';

  constructor(
    private table: Table,
    private router: Router
  ) {}

  ngAfterViewInit() {

    // Wait for table to be ready, then set up scroll handling
    this.setupScrollHandling();

    // Listen for page changes to handle scroll restoration after page switch
    this.table.onPage.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.pendingScrollRowId) {
        this.scrollToRowWhenReady(this.pendingScrollRowId);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.scrollableBody) {
      this.scrollableBody.removeEventListener('scroll', this.onScroll);
    }
  }

  private setupScrollHandling() {
    // Retry until scrollable body is found
    const maxAttempts = 50;
    let attempts = 0;

    const trySetup = () => {
      this.scrollableBody = this.getScrollableBody();
      if (this.scrollableBody && this.scrollableBody.children.length > 0) {
        if (!this.scrollEventAttached) {
          this.scrollableBody.addEventListener('scroll', this.onScroll, { passive: true });
          this.scrollEventAttached = true;

          // Always try to restore scroll position if there's saved state
          const savedPos = this.getSavedScrollPosition();
          if (savedPos && savedPos.rowId) {
            this.restoreScrollPosition();
          }
        }
      } else {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(trySetup, 100);
        }
      }
    };

    trySetup();
  }

  private getScrollableBody(): HTMLElement | null {
    const tableRoot = this.table?.el?.nativeElement;
    if (!tableRoot) {
      return null;
    }

    // Try common PrimeNG scrollable body class
    let scrollableBody = tableRoot.querySelector('.p-datatable-scrollable-body');
    if (!scrollableBody) {
      // Fallback: find any div with overflow: auto
      const divs = tableRoot.getElementsByTagName('div');
      for (let i = 0; i < divs.length; i++) {
        const style = window.getComputedStyle(divs[i]);
        if (
          style.overflow === 'auto' ||
          style.overflowY === 'auto' ||
          style.overflowX === 'auto'
        ) {
          scrollableBody = divs[i];
          break;
        }
      }
    }

    return scrollableBody as HTMLElement | null;
  }

  private onScroll = () => {
    this.saveScrollPosition();
  };

  private getStorageKey(): string {
    return `${this.storageKeyPrefix}${this.tableId}-${this.router.url}`;
  }

  private saveScrollPosition() {
    if (!this.scrollableBody) return;

    // Find the first visible row with data-row-id
    const allRows = Array.from(this.scrollableBody.querySelectorAll('tr')) as HTMLElement[];
    const dataRows = allRows.filter(
      (row) =>
        !row.closest('thead') &&
        !row.closest('tfoot') &&
        row.offsetParent !== null &&
        row.hasAttribute('data-row-id')
    );

    let rowId: string | undefined;
    for (const row of dataRows) {
      if (row.offsetTop + row.offsetHeight > this.scrollableBody.scrollTop) {
        rowId = row.getAttribute('data-row-id') || undefined;
        break;
      }
    }

    if (!rowId) return;

    const pos: ScrollPosition = {
      scrollTop: this.scrollableBody.scrollTop,
      scrollLeft: this.scrollableBody.scrollLeft,
      rowId,
    };

    sessionStorage.setItem(this.getStorageKey(), JSON.stringify(pos));
  }

  private getSavedScrollPosition(): ScrollPosition | null {
    const posStr = sessionStorage.getItem(this.getStorageKey());
    if (posStr) {
      try {
        return JSON.parse(posStr) as ScrollPosition;
      } catch {
        return null;
      }
    }
    return null;
  }

  private restoreScrollPosition() {
    // Wait for table to be fully rendered
    setTimeout(() => {
      const savedPos = this.getSavedScrollPosition();
      if (!savedPos || !savedPos.rowId || !this.scrollableBody) {
        return;
      }

      // Check if row is already in DOM and visible
      const row = this.scrollableBody.querySelector(
        `tr[data-row-id='${savedPos.rowId}']`
      ) as HTMLElement;

      if (row && row.offsetParent !== null) {
        row.scrollIntoView({ block: 'start', behavior: 'smooth' });
        return;
      }

      // Row not in DOM or not visible, check if we need to change page
      if (this.pTableScrollData && this.pTableScrollData.length > 0) {
        const dataIndex = this.pTableScrollData.findIndex(
          (item) => item[this.pTableScrollDataKey] == savedPos.rowId
        );

        if (dataIndex !== -1) {
          const pageSize = this.table.rows || 25;
          const targetPage = Math.floor(dataIndex / pageSize);
          const currentPage = Math.floor((this.table.first || 0) / pageSize);

          if (targetPage !== currentPage) {
            // Change to target page
            this.pendingScrollRowId = savedPos.rowId;
            this.table.first = targetPage * pageSize;
            this.table.onPageChange({ first: this.table.first, rows: pageSize });
            return;
          } else {
            // Row should be on current page, use retry mechanism
            this.scrollToRowWhenReady(savedPos.rowId);
            return;
          }
        }
      }

      // Fallback: restore scrollTop/scrollLeft with smooth scrolling
      this.scrollableBody.scrollTo({
        top: savedPos.scrollTop,
        left: savedPos.scrollLeft,
        behavior: 'smooth'
      });
    }, 500);
  }

  private scrollToRowWhenReady(rowId: string, maxRetries = 30, delay = 100) {
    let attempts = 0;

    const tryScroll = () => {
      if (!this.scrollableBody) {
        this.scrollableBody = this.getScrollableBody();
      }

      if (this.scrollableBody) {
        const row = this.scrollableBody.querySelector(
          `tr[data-row-id='${rowId}']`
        ) as HTMLElement;

        // Check if row exists and is visible (offsetParent !== null)
        if (row && row.offsetParent !== null) {
          row.scrollIntoView({ block: 'start', behavior: 'smooth' });
          this.pendingScrollRowId = null;
          return;
        }
      }

      attempts++;
      if (attempts < maxRetries) {
        setTimeout(tryScroll, delay);
      } else {
        // Final fallback with smooth scrolling
        const savedPos = this.getSavedScrollPosition();
        if (savedPos && this.scrollableBody) {
          this.scrollableBody.scrollTo({
            top: savedPos.scrollTop,
            left: savedPos.scrollLeft,
            behavior: 'smooth'
          });
        }
        this.pendingScrollRowId = null;
      }
    };

    tryScroll();
  }

  /**
   * Manually trigger scroll restoration (e.g., after data is loaded)
   */
  public restoreScroll() {
    this.restoreScrollPosition();
  }

  /**
   * Clear saved scroll position for this table
   */
  public clearScrollPosition() {
    sessionStorage.removeItem(this.getStorageKey());
  }
}

